import React, { useState, useEffect } from 'react';
import { Box, Alert, IconButton } from '@mui/material';
import { Close as CloseIcon } from '@mui/icons-material';

// Default API base URL - can be overridden via props
const DEFAULT_API_BASE_URL = 'https://admin.wurknest.com:6150/api';

const AnnouncementBar = ({
  pageName = '',
  apiBaseUrl = DEFAULT_API_BASE_URL,
  onClose
}) => {
  const [announcements, setAnnouncements] = useState([]);
  const [dismissedIds, setDismissedIds] = useState(new Set());
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);

  // Load dismissed announcements from localStorage
  useEffect(() => {
    const dismissed = localStorage.getItem('dismissedAnnouncements');
    if (dismissed) {
      try {
        setDismissedIds(new Set(JSON.parse(dismissed)));
      } catch (error) {
        console.error('Error loading dismissed announcements:', error);
      }
    }
  }, []);

  // Fetch announcements
  useEffect(() => {
    const fetchAnnouncements = async () => {
      try {
        setIsLoading(true);
        setError(null);

        const response = await fetch(`${apiBaseUrl}/announcements/active?pageName=${pageName}`);

        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }

        const data = await response.json();
        setAnnouncements(data?.data || []);
      } catch (err) {
        console.error('Error fetching announcements:', err);
        setError(err.message);
        setAnnouncements([]);
      } finally {
        setIsLoading(false);
      }
    };

    fetchAnnouncements();

    // Refetch every 5 minutes
    const interval = setInterval(fetchAnnouncements, 5 * 60 * 1000);
    return () => clearInterval(interval);
  }, [pageName, apiBaseUrl]);

  // Filter out dismissed announcements
  const visibleAnnouncements = announcements.filter(
    announcement => !dismissedIds.has(announcement._id)
  );

  const handleDismiss = (announcementId) => {
    const newDismissed = new Set(dismissedIds);
    newDismissed.add(announcementId);
    setDismissedIds(newDismissed);

    // Save to localStorage
    localStorage.setItem('dismissedAnnouncements', JSON.stringify([...newDismissed]));

    if (onClose) {
      onClose(announcementId);
    }
  };

  const handleClick = (announcement) => {
    if (announcement.onClick) {
      try {
        // Execute the onClick JavaScript code
        eval(announcement.onClick);
      } catch (error) {
        console.error('Error executing onClick:', error);
      }
    }
  };

  // Don't render if loading, error, or no announcements
  if (isLoading || error || visibleAnnouncements.length === 0) {
    return null;
  }

  return (
    <Box>
      {visibleAnnouncements.map((announcement) => (
        <Alert
          key={announcement._id}
          severity="info"
          action={
            <IconButton
              size="small"
              onClick={() => handleDismiss(announcement._id)}
              sx={{ color: 'inherit' }}
            >
              <CloseIcon fontSize="small" />
            </IconButton>
          }
          onClick={() => handleClick(announcement)}
          sx={{
            mb: 1,
            cursor: announcement.onClick ? 'pointer' : 'default',
            '&:hover': announcement.onClick ? {
              backgroundColor: 'rgba(0, 0, 0, 0.04)'
            } : {}
          }}
        >
          <strong>{announcement.name}</strong>
          {announcement.text && (
            <>
              <br />
              {announcement.text}
            </>
          )}
        </Alert>
      ))}
    </Box>
  );
};

export default AnnouncementBar;